import styled from 'styled-components';

// Styled Components
const Container = styled.div`
  width: 100%;
  padding-bottom: 12px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
  font-size: var(--font-size-20);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-900);
  margin: 0;
`;

const CloseButton = styled.button`
  background: var(--color-gray-400);
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  color: var(--color-white);
`;

const Description = styled.p`
  color: var(--color-gray-400);
  font-size: var(--font-size-14);
  margin-bottom: 24px;
  line-height: 1.5;
`;

const FileCountContainer = styled.div`
  margin-bottom: 16px;
`;

const FileCountText = styled.span`
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-700);
`;

// Upload zone styles moved to UploadZone.styled.ts

// File Preview Styles
const FilePreviewSection = styled.div`
  margin-top: 24px;
`;

const FileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 9px;
  margin-top: 16px;
`;

const FilePreviewCard = styled.div`
  position: relative;
  max-width: 110px;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
  background: var(--color-white);
  transition: var(--transition-normal);
  /* Forces Safari to create a GPU compositing layer, which fixes the
     pointer-event / hover hit-area misalignment bug that occurs after
     a delete → re-upload cycle */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  /* Isolate stacking context so Safari correctly resolves z-index of children */
  isolation: isolate;

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const FilePreview = styled.div`
  width: 100%;
  height: 108px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-gray-50);
  position: relative;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  overflow: hidden;
`;

const FileCardName = styled.div`
  padding: 6px 8px;
  font-size: 10px;
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-700);
  background: var(--color-white);
  border-top: 1px solid var(--color-gray-200);
  text-align: center;
  word-break: break-word;
  line-height: 1.2;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const FileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FileIcon = styled.div`
  width: 40px;
  height: 40px;
  background: var(--color-primary-100);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary-500);
`;

const FileActions = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 4px;
  z-index: 2;
  pointer-events: auto;
  /* Promote to its own layer so Safari hit-testing is re-evaluated correctly */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
`;

const ActionButton = styled.button`
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: var(--radius-base);
  padding: 4px;
  cursor: pointer;
  color: var(--color-white);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition-fast);
  position: relative;
  z-index: 2;
  /* Safari: force compositing layer on each button so hit area matches visual */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const FileName = styled.div`
  padding: 8px;
  font-size: var(--font-size-14);
  color: var(--color-gray-700);
  font-weight: var(--font-weight-medium);
  text-align: center;
  word-break: break-word;
  line-height: 1.2;
`;

// Preview Modal Styles
const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  padding: 16px;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: var(--radius-lg);
`;

const PreviewDocument = styled.div`
  width: 100%;
  height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-gray-50);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-gray-200);
`;

const DocumentIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: var(--color-gray-400);
`;

const DocumentText = styled.p`
  font-size: var(--font-size-16);
  color: var(--color-gray-600);
  margin: 0;
`;

const PreviewFileName = styled.h3`
  font-size: var(--font-size-18);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-900);
  margin: 0;
  text-align: center;
  word-break: break-word;
`;

const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--color-gray-200);
`;

const SaveButton = styled.button<{ disabled?: boolean }>`
  background: ${props =>
    props.disabled ? 'var(--color-gray-300)' : 'var(--color-primary-500)'};
  color: var(--color-white);
  border: none;
  padding: 12px 24px;
  border-radius: var(--radius-md);
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-medium);
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props =>
      props.disabled ? 'var(--color-gray-300)' : 'var(--color-primary-600)'};
  }

  &:disabled {
    background: var(--color-gray-300);
    cursor: not-allowed;
  }
`;

const ConfirmationModalContent = styled.div`
  text-align: center;
  padding: 20px;
`;

const ConfirmationTitle = styled.h3`
  font-size: var(--font-size-18);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-900);
  margin: 0 0 12px 0;
`;

const ConfirmationMessage = styled.p`
  font-size: var(--font-size-14);
  color: var(--color-gray-600);
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ConfirmationButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: var(--radius-md);
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid;

  ${props =>
    props.variant === 'primary'
      ? `
    background: var(--color-primary-500);
    color: var(--color-white);
    border-color: var(--color-primary-500);

    &:hover {
      background: var(--color-primary-600);
      border-color: var(--color-primary-600);
    }
  `
      : `
    background: var(--color-white);
    color: var(--color-gray-700);
    border-color: var(--color-gray-300);

    &:hover {
      background: var(--color-gray-50);
      border-color: var(--color-gray-400);
    }
  `}
`;

export {
  Container,
  ModalHeader,
  ModalTitle,
  CloseButton,
  Description,
  FileCountContainer,
  FileCountText,
  FilePreviewSection,
  FileGrid,
  FilePreviewCard,
  FilePreview,
  FileCardName,
  FileImage,
  FileIcon,
  FileActions,
  ActionButton,
  FileName,
  PreviewContainer,
  PreviewImage,
  PreviewDocument,
  DocumentIcon,
  DocumentText,
  PreviewFileName,
  SaveButtonContainer,
  SaveButton,
  ConfirmationModalContent,
  ConfirmationTitle,
  ConfirmationMessage,
  ConfirmationButtons,
  ConfirmationButton,
};
